import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useParams, useHistory } from "react-router-dom";
import { styled } from "@mui/material/styles";
import {
  Grid,
  Paper,
  Typography,
  Divider,
  Box,
  Tooltip,
  IconButton,
  Button,
  Card,
  CardContent,
  Chip,
  useTheme,
  alpha,
  Badge,
  Avatar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  Snackbar,
  Alert,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import {
  approveSubOrderPayment,
  completeSubOrder,
  confirmCompleteSuborder,
  deleteSubOrderPayment,
  getSubOrderById,
  markAsPaidSubOrderPayment,
  sendForApprovalSubOrderPayment,
  updateSubOrderLineItem,
  validateSubOrder,
  sendForApproval,
  subOrderApproval,
  // NEW IMPORTS - Add these to your service layer
  receiveOutputLineItem,
  checkReceivedBatch,
  getLineItemReceives,
  getSubOrderReceivingProgress,
  getAllDamagedBatches,
  // RECONSTRUCTION IMPORTS
  createReconstructionBatch,
  getReconstructionsByCheckId,
  updateReconstructionStatus,
  forceDeleteSubOrder,
  deleteSubOrderLineItem,
  rejectSubOrderLineItemDeletion,
  requestSubOrderLineItemDeletion,
} from "../../../services/SubOrder";
import SubOrderPayment from "./SubOrderPayment";

// NEW COMPONENT IMPORTS
import ReceivingProgressCard from "./ReceivingProgressCard";
import ReceiveOutputDialog from "./ReceiveOutputDialog";
import CheckReceivedBatchDialog from "./CheckReceivedBatchDialog";
import ReceivingHistoryDialog from "./ReceivingHistoryDialog";
import OutputItemsTable from "./OutputItemsTable";
import EffectiveMaterialsDialog from "./EffectiveMaterialsDialog";
import DamagedItemsPanel from "./DamagedItemsPanel";
import ReconstructionDialog from "./ReconstructionDialog";
import ReconstructionHistoryDialog from "./ReconstructionHistoryDialog";

// Icons
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ShoppingBasketIcon from "@mui/icons-material/ShoppingBasket";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import TagIcon from "@mui/icons-material/Tag";
import PaymentIcon from "@mui/icons-material/Payment";
import AddPaymentIcon from "@mui/icons-material/RequestQuoteOutlined";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import EditIcon from "@mui/icons-material/Edit";
import SaveIcon from "@mui/icons-material/Save";
import VisibilityIcon from "@mui/icons-material/Visibility";
import TableItem from "../../../components/TableItem";
import BillWritableTable from "../../../components/BillWritableTable";
import { getImportsByCategory, getImportItemById } from "../../../services/Import";
import { getCurrentPrice } from "../../../services/ImportPrice";
import CategoryItemsViewDialog from "../../../components/CategoryItemsViewDialog";
import ItemSelectingPopup from "../../../components/ItemSelectingPopup";
import SubOrderPaymentCard from "./SubOrderPaymentCard";
import DoneIcon from "@mui/icons-material/Done";
import SendIcon from "@mui/icons-material/Send";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import { RoleBasedRender } from "../../../components/RoleBaseRender";
import { hasAnyRole } from "../../../Utils/Common";

const SelectButton = styled(Button)(({ theme }) => ({
  borderRadius: theme.shape.borderRadius,
  textTransform: "none",
  minWidth: "100%",
  justifyContent: "flex-start",
  padding: theme.spacing(1),
  backgroundColor: alpha(theme.palette.primary.light, 0.1),
  color: theme.palette.text.primary,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.light, 0.2),
    borderColor: theme.palette.primary.main,
  },
}));

function SubOrderView() {
  const theme = useTheme();
  const history = useHistory();
  const { orderId } = useParams();
  const [subOrder, setSubOrder] = useState(null);
  const [isLoading, setLoading] = useState(false);
  const [errors, setErrors] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [inputItems, setInputItems] = useState([]);
  const [outputItems, setOutputItems] = useState([]);
  const [paymentList, setPaymentList] = useState([]);
  const [enablePayment, setEnablePayment] = useState(false);
  const [selectedPaymentId, setSelectedPaymentId] = useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [confirmationInput, setConfirmationInput] = useState("");
  const [editInput, setEditInput] = useState(false);
  const [editOutput, setEditOutput] = useState(false);

  // STATE for validation and approval dialogs
  const [validationDialog, setValidationDialog] = useState(false);
  const [insufficientItems, setInsufficientItems] = useState([]);
  const [validationDialogMessage, setValidationDialogMessage] = useState("");
  const [approvalDialog, setApprovalDialog] = useState(false);
  const [approvalType, setApprovalType] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");

  // Force delete (wipe) state
  const [forceDeleteOpen, setForceDeleteOpen] = useState(false);
  const [forceDeleteConfirmText, setForceDeleteConfirmText] = useState("");
  const [forceDeleteLoading, setForceDeleteLoading] = useState(false);

  // NEW STATE for receiving and checking
  const [receivingProgress, setReceivingProgress] = useState([]);
  const [receiveDialogOpen, setReceiveDialogOpen] = useState(false);
  const [checkDialogOpen, setCheckDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedLineItem, setSelectedLineItem] = useState(null);
  const [selectedReceiveBatch, setSelectedReceiveBatch] = useState(null);
  const [lineItemReceives, setLineItemReceives] = useState([]);
  const [damagedBatches, setDamagedBatches] = useState([]);

  // NEW STATE for reconstruction
  const [reconstructionDialogOpen, setReconstructionDialogOpen] =
    useState(false);
  const [reconstructionHistoryOpen, setReconstructionHistoryOpen] =
    useState(false);
  const [selectedCheckRecord, setSelectedCheckRecord] = useState(null);
  const [reconstructionHistory, setReconstructionHistory] = useState([]);

  // Delete category item dialog state
  const [deleteCategoryItemDialogOpen, setDeleteCategoryItemDialogOpen] = useState(false);
  const [pendingDeleteRow, setPendingDeleteRow] = useState(null);
  const [pendingDeleteType, setPendingDeleteType] = useState(null);

  // Pending deletion approval dialog state (for authorized users)
  const [pendingApprovalDialogOpen, setPendingApprovalDialogOpen] = useState(false);
  const [pendingApprovalItem, setPendingApprovalItem] = useState(null);
  const [pendingApprovalAction, setPendingApprovalAction] = useState(null); // 'approve' | 'reject'

  const [inputCategoryItems, setInputCategoryItems] = useState([]);
  const [outputCategoryItems, setOutputCategoryItems] = useState([]);
  const [categoryViewOpen, setCategoryViewOpen] = useState(false);
  const [categoryViewName, setCategoryViewName] = useState("");
  const [categoryViewItems, setCategoryViewItems] = useState([]);

  // Category item select popup (edit mode)
  const [openCategoryItemSelect, setOpenCategoryItemSelect] = useState(false);
  const [activeCategoryRow, setActiveCategoryRow] = useState(null);
  const [categoryPopupSelectedItems, setCategoryPopupSelectedItems] = useState([]);
  const [categoryPopupRows, setCategoryPopupRows] = useState([]);

  // Effective materials state: catId → [importId, ...]
  const [effectiveMaterialsMap, setEffectiveMaterialsMap] = useState({});
  const [effectiveMaterialsDialogOpen, setEffectiveMaterialsDialogOpen] = useState(false);
  const [currentEffectiveCategory, setCurrentEffectiveCategory] = useState(null);

  // Input item prices fetched for material cost calculation: importId → price
  const [inputItemPrices, setInputItemPrices] = useState({});

  const [writeable_page, set_writeable_Page] = React.useState(0);
  const [writeable_rowsPerPage, set_writeable_RowsPerPage] =
    React.useState(100);

  const columnsForInput = [
    { id: "item", label: "Item", minWidth: 100, editable: false },
    { id: "brand", label: "Brand", minWidth: 100, editable: false },
    {
      id: "qty",
      label: "Qty",
      minWidth: 100,
      editable: true,
      type: "number",
      isDecimal: false,
    },
    { id: "orderUnit", label: "Order Unit", minWidth: 100, editable: true },
    { id: "unitType", label: "Item Unit", minWidth: 100, editable: true },
    {
      id: "itemUintsPerOrderUnit",
      label: "Item Units Amount",
      minWidth: 100,
      editable: true,
      type: "number",
      isDecimal: true,
    },
    {
      id: "availability",
      label: "Supplied",
      minWidth: 100,
      editable: true,
      type: "checkbox",
      isDecimal: true,
    },
  ];

  const columnsForOutput = [
    { id: "item", label: "Item", minWidth: 100, editable: false },
    { id: "brand", label: "Brand", minWidth: 100, editable: false },
    {
      id: "qty",
      label: "Expected Qty",
      minWidth: 100,
      editable: true,
      type: "number",
      isDecimal: false,
    },
    { id: "orderUnit", label: "Order Unit", minWidth: 100, editable: false },
    { id: "unitType", label: "Item Unit", minWidth: 100, editable: false },
    {
      id: "itemUintsPerOrderUnit",
      label: "Item Units Amount",
      minWidth: 100,
      editable: false,
      type: "number",
      isDecimal: true,
    },
    {
      id: "availability",
      label: "Received Qty",
      minWidth: 100,
      editable: true,
      type: "checkbox",
      isDecimal: true,
    },
  ];

  function createDataForSelectedInputTable(
    item,
    brand,
    qty,
    orderUnit,
    unitType,
    itemUintsPerOrderUnit,
    availability,
    lineId,
    existingQty,
    importId,
    fromCategoryId,
    rawItemName,
    pendingDeletion = false
  ) {
    return {
      item,
      brand,
      qty,
      orderUnit,
      unitType,
      itemUintsPerOrderUnit,
      availability,
      lineId,
      existingQty,
      importId,
      fromCategoryId,
      rawItemName,
      pendingDeletion,
    };
  }

  const handleDeleteCategoryItem = (row, type) => {
    setPendingDeleteRow(row);
    setPendingDeleteType(type);
    setDeleteCategoryItemDialogOpen(true);
  };

  const handleConfirmDeleteCategoryItem = async () => {
    if (!pendingDeleteRow) return;
    setDeleteCategoryItemDialogOpen(false);
    setLoading(true);

    if (!pendingDeleteRow.lineId) {
      // Unsaved item — remove from local state only
      if (pendingDeleteType === "INPUT") {
        setInputItems((prev) => prev.filter((r) => r !== pendingDeleteRow));
      } else {
        setOutputItems((prev) => prev.filter((r) => r !== pendingDeleteRow));
      }
    } else if (subOrder?.status === "DRAFT") {
      // DRAFT — any user may delete directly
      const success = await deleteSubOrderLineItem(pendingDeleteRow.lineId);
      if (!success) {
        setErrors("Failed to remove item. Please try again.");
        setSnackbarOpen(true);
      } else {
        await fetchData();
      }
    } else {
      // APPROVED+ — submit a deletion request; item stays until an authorized person approves
      await requestSubOrderLineItemDeletion(pendingDeleteRow.lineId);
      await fetchData();
    }

    setLoading(false);
    setPendingDeleteRow(null);
    setPendingDeleteType(null);
  };

  const handleOpenPendingApproval = (item, action) => {
    setPendingApprovalItem(item);
    setPendingApprovalAction(action);
    setPendingApprovalDialogOpen(true);
  };

  const handleSubmitPendingApproval = async () => {
    if (!pendingApprovalItem) return;
    setPendingApprovalDialogOpen(false);
    setLoading(true);

    if (pendingApprovalAction === "approve") {
      const success = await deleteSubOrderLineItem(pendingApprovalItem.lineId);
      if (!success) {
        setErrors("Failed to approve deletion. Please try again.");
        setSnackbarOpen(true);
      }
    } else {
      await rejectSubOrderLineItemDeletion(pendingApprovalItem.lineId);
    }

    setPendingApprovalItem(null);
    setPendingApprovalAction(null);
    setLoading(false);
    await fetchData();
  };

  const handleViewCategoryItems = async (catId, catName) => {
    const items = await getImportsByCategory(catId);
    setCategoryViewName(catName);
    setCategoryViewItems(Array.isArray(items) ? items : []);
    setCategoryViewOpen(true);
  };

  const handleOpenCategoryItemSelect = (catId, catName, forType) => {
    if (forType === "OUTPUT") {
      // Block if there are no category-related input items of any kind in this order
      const anyCatInputItems = inputItems.filter((i) => i.fromCategoryId != null);
      if (anyCatInputItems.length === 0) {
        setErrors(
          `Cannot add "${catName}" as an output category: no category-related input items exist in this order.`
        );
        setSnackbarOpen(true);
        return;
      }
      setCurrentEffectiveCategory({ catId, catName });
      setEffectiveMaterialsDialogOpen(true);
      return;
    }

    const existingRows = inputItems;
    const preSelected = existingRows
      .filter((r) => r.fromCategoryId === catId)
      .map((r) => r.importId);
    setCategoryPopupSelectedItems(preSelected);
    setActiveCategoryRow({ catId, catName, forType });
    setOpenCategoryItemSelect(true);
  };

  const handleConfirmEffectiveMaterials = (selectedMaterialIds) => {
    setEffectiveMaterialsDialogOpen(false);
    const { catId, catName } = currentEffectiveCategory;
    setEffectiveMaterialsMap((prev) => ({ ...prev, [catId]: selectedMaterialIds }));
    // Now open the regular item-selecting popup for OUTPUT
    const preSelected = outputItems
      .filter((r) => r.fromCategoryId === catId)
      .map((r) => r.importId);
    setCategoryPopupSelectedItems(preSelected);
    setActiveCategoryRow({ catId, catName, forType: "OUTPUT" });
    setOpenCategoryItemSelect(true);
  };

  const handleCloseCategoryItemSelect = async () => {
    setOpenCategoryItemSelect(false);
    if (!activeCategoryRow) return;
    const { catId, catName, forType } = activeCategoryRow;
    const selectedSet = new Set(categoryPopupSelectedItems);

    let newInputRows = forType === "INPUT"
      ? inputItems.filter((r) => r.fromCategoryId !== catId || selectedSet.has(r.importId))
      : [...inputItems];
    let newOutputRows = forType === "OUTPUT"
      ? outputItems.filter((r) => r.fromCategoryId !== catId || selectedSet.has(r.importId))
      : [...outputItems];

    for (const importId of categoryPopupSelectedItems) {
      if (
        newInputRows.some((r) => r.importId === importId) ||
        newOutputRows.some((r) => r.importId === importId)
      ) continue;

      const importItem = await getImportItemById(importId);
      const itemLabel = (
        <Box display="flex" alignItems="center" gap={0.5}>
          {importItem.itemName + " (" + importItem.product_code + ")"}
          <Chip
            size="small"
            label={catName}
            color="secondary"
            variant="outlined"
            sx={{ height: 16, fontSize: "0.65rem", "& .MuiChip-label": { px: 0.5 } }}
          />
        </Box>
      );

      const rawName = `${importItem.itemName}(${importItem.product_code})`;
      if (forType === "INPUT") {
        newInputRows.push(
          createDataForSelectedInputTable(
            itemLabel, importItem.brand, 0, importItem.unitType,
            importItem.unitType, 1.0, false, null, importItem.qty,
            importItem.importId, catId, rawName, false
          )
        );
      } else {
        newOutputRows.push(
          createDataForSelectedInputTable(
            itemLabel, importItem.brand, 0, importItem.unitType,
            importItem.unitType, 1.0, false, null, importItem.qty,
            importItem.importId, catId, rawName, false
          )
        );
      }
    }

    setInputItems(newInputRows);
    setOutputItems(newOutputRows);
    setActiveCategoryRow(null);
  };

  const handleInputRowsChange = (newRows) => {
    const insufficient = newRows
      .filter((newRow, idx) => {
        const oldRow = inputItems[idx];
        const wasSupplied = oldRow?.availability === "Yes" || oldRow?.availability === true;
        const isNowSupplied = newRow.availability === "Yes" || newRow.availability === true;
        return isNowSupplied && !wasSupplied;
      })
      .filter((row) => {
        const required =
          parseFloat(row.qty || 0) * parseFloat(row.itemUintsPerOrderUnit || 1);
        const available = parseFloat(row.existingQty || 0);
        return required > available;
      })
      .map((row) => ({
        orderedQty: parseFloat(row.qty || 0),
        prodUnitsForItemUnit: parseFloat(row.itemUintsPerOrderUnit || 1),
        imports: {
          qty: parseFloat(row.existingQty || 0),
          itemName: row.rawItemName || row.brand,
          brand: row.brand,
          unitType: row.unitType,
        },
      }));

    if (insufficient.length > 0) {
      setInsufficientItems(insufficient);
      setValidationDialogMessage(
        "Cannot mark as supplied. The following items have insufficient inventory:"
      );
      setValidationDialog(true);
      return; // block the checkbox change
    }

    setInputItems(newRows);
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      let result = await getSubOrderById(orderId);
      if (result !== "Something went wrong...") {
        setSubOrder(result);

        const allLineItems = result?.lineItems ?? [];

        // Build a map of catId → catName from category-type line items
        const categoryMap = {};
        allLineItems
          .filter((li) => li.importCategory != null)
          .forEach((li) => {
            categoryMap[li.importCategory.cat_id] = li.importCategory.category;
          });

        const mapImportItem = (li) => {
          const matchedCat = li.imports?.im_categories?.find(
            (c) => categoryMap[c.cat_id] != null
          );
          const catId = matchedCat?.cat_id ?? null;
          const catName = catId != null ? categoryMap[catId] : null;
          const itemLabel = catName != null ? (
            <Box display="flex" alignItems="center" gap={0.5}>
              {li.imports.itemName + " (" + li.imports.product_code + ")"}
              <Chip
                size="small"
                label={catName}
                color="secondary"
                variant="outlined"
                sx={{ height: 16, fontSize: "0.65rem", "& .MuiChip-label": { px: 0.5 } }}
              />
            </Box>
          ) : `${li.imports.itemName}(${li.imports.product_code})`;

          return createDataForSelectedInputTable(
            itemLabel,
            li.imports.brand,
            li.orderedQty,
            li.itemUnit,
            li.imports.unitType,
            li.prodUnitsForItemUnit,
            li.supplied === true ? "Yes" : "No",
            li.id,
            li.imports.qty,
            li.imports.importId,
            catName != null ? catId : null,
            `${li.imports.itemName}(${li.imports.product_code})`,
            li.pendingDeletion ?? false
          );
        };
        const mapCategoryItem = (li) => ({
          lineItemId: li.id,
          categoryId: li.importCategory.cat_id,
          categoryName: li.importCategory.category,
        });

        setInputItems(
          allLineItems
            .filter((li) => li.lineItemType === "INPUT" && li.imports != null)
            .map(mapImportItem)
        );
        setInputCategoryItems(
          allLineItems
            .filter((li) => li.lineItemType === "INPUT" && li.importCategory != null)
            .map(mapCategoryItem)
        );
        setOutputItems(
          allLineItems
            .filter((li) => li.lineItemType === "OUTPUT" && li.imports != null)
            .map(mapImportItem)
        );
        setOutputCategoryItems(
          allLineItems
            .filter((li) => li.lineItemType === "OUTPUT" && li.importCategory != null)
            .map(mapCategoryItem)
        );

        // Load effective materials map from persisted line item data
        const loadedEffectiveMap = {};
        allLineItems
          .filter((li) => li.lineItemType === "OUTPUT" && li.imports != null && li.effectiveMaterialImportIds?.length > 0)
          .forEach((li) => {
            const matchedCat = li.imports?.im_categories?.find((c) => categoryMap[c.cat_id] != null);
            if (matchedCat) {
              loadedEffectiveMap[matchedCat.cat_id] = li.effectiveMaterialImportIds;
            }
          });
        setEffectiveMaterialsMap(loadedEffectiveMap);

        setPaymentList(result.subOrderPayment || []);

        // Fetch receiving progress and damaged batches (only if order is beyond DRAFT)
        if (result.status !== "DRAFT") {
          let progress = [];
          let orderDamaged = [];

          try {
            const progressResult = await getSubOrderReceivingProgress(orderId);
            if (progressResult && Array.isArray(progressResult)) {
              progress = progressResult;
            }
          } catch (error) {
            console.error("[RECEIVING PROGRESS] Error:", error);
          }

          try {
            const damaged = await getAllDamagedBatches();
            if (damaged && Array.isArray(damaged)) {
              const currentOrderId = parseInt(orderId);
              orderDamaged = damaged.filter(
                (batch) =>
                  batch.receive?.subOrderLineItem?.subOrderId === currentOrderId
              );
              setDamagedBatches(orderDamaged);
            }
          } catch (error) {
            console.error("[DAMAGED BATCHES] Error:", error);
          }

          // Adjust totalDamagedQty in progress by subtracting items recovered
          // through COMPLETED reconstructions
          const adjustedProgress = progress.map((p) => {
            const lineItemDamaged = orderDamaged.filter(
              (b) => b.receive?.subOrderLineItem?.id === p.lineItemId
            );
            const recoveredQty = lineItemDamaged.reduce((sum, batch) => {
              return (
                sum +
                (batch.reconstructions || [])
                  .filter((r) => r.status === "COMPLETED")
                  .reduce((s, r) => s + (r.reconstructedQty || 0), 0)
              );
            }, 0);
            return {
              ...p,
              totalDamagedQty: Math.max(
                0,
                (p.totalDamagedQty || 0) - recoveredQty
              ),
            };
          });

          setReceivingProgress(adjustedProgress);
        }
        return result;
      } else {
        setErrors("Unable to fetch sub order data!");
      }
    } catch (error) {
      console.error("Error fetching sub order data:", error);
      setErrors("An error occurred while fetching sub order data");
    } finally {
      setLoading(false);
    }
    return null;
  };

  useEffect(() => {
    fetchData();
  }, [orderId, enablePayment]);

  // Fetch current prices for all input items whenever the list changes
  const inputItemIdKey = useMemo(
    () => inputItems.map((i) => i.importId).filter(Boolean).join(","),
    [inputItems]
  );
  useEffect(() => {
    if (!inputItemIdKey) return;
    const fetchPrices = async () => {
      const newPrices = {};
      for (const id of inputItemIdKey.split(",")) {
        const importId = parseInt(id);
        const priceData = await getCurrentPrice(importId);
        if (priceData && priceData !== "Something went wrong..." && priceData.price != null) {
          newPrices[importId] = priceData.price;
        }
      }
      setInputItemPrices(newPrices);
    };
    fetchPrices();
  }, [inputItemIdKey]);

  // Compute per-output-item material unit cost and total cost
  const outputItemCosts = useMemo(() => {
    const map = {};
    for (const progress of receivingProgress) {
      const outputItem = outputItems.find((oi) => oi.lineId === progress.lineItemId);
      if (!outputItem) continue;

      const catId = outputItem.fromCategoryId;
      // For category output items, use only selected effective input items + all non-category items.
      // For non-category output items, use all input items.
      const effectiveIds = catId != null ? (effectiveMaterialsMap[catId] ?? null) : null;

      const calcSteps = [];
      let materialCost = 0;
      for (const input of inputItems) {
        const price = inputItemPrices[input.importId];
        if (price == null) continue;
        const isNonCategory = input.fromCategoryId == null;
        const isEffective = effectiveIds == null || effectiveIds.includes(input.importId);
        if (isNonCategory || isEffective) {
          const qty = parseFloat(input.qty || 0);
          const conversion = parseFloat(input.itemUintsPerOrderUnit || 1);
          const contribution = qty * conversion * price;
          materialCost += contribution;
          calcSteps.push({ name: input.rawItemName || input.item, qty, conversion, price, contribution });
        }
      }

      const expectedQty = parseFloat(outputItem.qty) || 1;
      const materialUnitCost = materialCost / expectedQty;
      const totalCost = materialUnitCost * (progress.totalReceivedQty || 0);
      // Collect all input items from the same category — these are the category-selected
      // input items that belong to the material cost calculation for this output.
      const effectiveMaterials =
        catId != null ? inputItems.filter((i) => i.fromCategoryId === catId) : [];
      map[progress.lineItemId] = { materialUnitCost, totalCost, effectiveMaterials, calcSteps, expectedQty, isCategoryOutput: catId != null };
    }
    return map;
  }, [receivingProgress, outputItems, inputItems, inputItemPrices, effectiveMaterialsMap]);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  const filterDate = (dateString) => {
    if (!dateString) return "N/A";
    try {
      let dateSlpit = dateString.split(" ");
      const date = dateSlpit[0];
      return date;
    } catch (error) {
      console.error("Error formatting date:", error);
      return dateString;
    }
  };

  const formatCurrency = (value) => {
    return typeof value === "number"
      ? `Rs: ${value.toFixed(2)}`
      : value
      ? `Rs: ${parseFloat(value).toFixed(2)}`
      : "N/A";
  };

  const handleOpenPaymentModal = () => {
    setEnablePayment(true);
  };

  const getStatusChip = (status) => {
    let color;
    let bgColor;
    let label = status || "Unknown";

    switch (status?.toUpperCase()) {
      case "DRAFT":
        color = theme.palette.grey[600];
        bgColor = alpha(theme.palette.grey[600], 0.1);
        break;
      case "AWAITING_APPROVAL":
        color = theme.palette.warning.main;
        bgColor = alpha(theme.palette.warning.main, 0.1);
        label = "Awaiting Approval";
        break;
      case "APPROVED":
        color = theme.palette.success.main;
        bgColor = alpha(theme.palette.success.main, 0.1);
        break;
      case "REJECTED":
        color = theme.palette.error.main;
        bgColor = alpha(theme.palette.error.main, 0.1);
        break;
      case "AWAITING_FOR_SUPPLY_ITEMS":
        color = theme.palette.info.main;
        bgColor = alpha(theme.palette.info.main, 0.1);
        label = "Awaiting Supply";
        break;
      case "PROCESSING":
        color = theme.palette.primary.main;
        bgColor = alpha(theme.palette.primary.main, 0.1);
        break;
      // Legacy values — order status no longer transitions to these (payment is tracked
      // separately via paymentStatus), kept only to render any not-yet-migrated orders.
      case "AWAITING_FOR_PAYMENT":
        color = theme.palette.warning.main;
        bgColor = alpha(theme.palette.warning.main, 0.1);
        label = "Awaiting Payment";
        break;
      case "PAID":
        color = theme.palette.success.main;
        bgColor = alpha(theme.palette.success.main, 0.1);
        break;
      case "COMPLETED":
        color = theme.palette.success.dark;
        bgColor = alpha(theme.palette.success.dark, 0.1);
        label = "Completed";
        break;
      default:
        color = theme.palette.grey[600];
        bgColor = alpha(theme.palette.grey[600], 0.1);
    }

    return (
      <Chip
        label={label}
        size="small"
        sx={{
          bgcolor: bgColor,
          color: color,
          fontWeight: 600,
          borderRadius: 1,
          px: 1,
        }}
      />
    );
  };

  // Payment status is tracked independently of the order's workflow status above —
  // it reflects paidAmount vs cost only (AWAITING_PAYMENT / PARTIALLY_PAID / PAID).
  const getPaymentStatusChip = (paymentStatus) => {
    let color;
    let bgColor;
    let label;

    switch (paymentStatus?.toUpperCase()) {
      case "PARTIALLY_PAID":
        color = theme.palette.warning.main;
        bgColor = alpha(theme.palette.warning.main, 0.1);
        label = "Partially Paid";
        break;
      case "PAID":
        color = theme.palette.success.main;
        bgColor = alpha(theme.palette.success.main, 0.1);
        label = "Paid";
        break;
      case "AWAITING_PAYMENT":
      default:
        color = theme.palette.grey[600];
        bgColor = alpha(theme.palette.grey[600], 0.1);
        label = "Awaiting Payment";
    }

    return (
      <Chip
        label={label}
        size="small"
        sx={{
          bgcolor: bgColor,
          color: color,
          fontWeight: 600,
          borderRadius: 1,
          px: 1,
        }}
      />
    );
  };

  const handleForceDelete = async () => {
    setForceDeleteLoading(true);
    const success = await forceDeleteSubOrder(orderId);
    setForceDeleteLoading(false);
    if (success) {
      setForceDeleteOpen(false);
      history.goBack();
    } else {
      setErrors("Failed to wipe sub order. Please try again.");
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedPaymentId(id);
    setOpenDeleteDialog(true);
  };

  const handleDeleteConfirm = () => {
    if (selectedPaymentId) {
      deletePayment(selectedPaymentId);
      setOpenDeleteDialog(false);
      setConfirmationInput("");
    }
  };

  const deletePayment = async (paymentId) => {
    let deleteResponse = await deleteSubOrderPayment(paymentId);
    if (!deleteResponse) {
      setErrors("Unable to delete order payment...");
    } else {
      fetchData();
    }
  };

  const handleCopyId = () => {
    navigator.clipboard.writeText(selectedPaymentId);
  };

  const saveLineItems = async (type, items) => {
    console.log("Save Line Items", items);

    // Validate new INPUT items that are marked as supplied
    if (type === "INPUT") {
      const insufficient = items
        .filter(
          (row) =>
            !row.lineId &&
            row.importId &&
            (row.availability === "Yes" || row.availability === true)
        )
        .filter((row) => {
          const required =
            parseFloat(row.qty || 0) * parseFloat(row.itemUintsPerOrderUnit || 1);
          const available = parseFloat(row.existingQty || 0);
          return required > available;
        })
        .map((row) => ({
          orderedQty: parseFloat(row.qty || 0),
          prodUnitsForItemUnit: parseFloat(row.itemUintsPerOrderUnit || 1),
          imports: {
            qty: parseFloat(row.existingQty || 0),
            itemName: row.rawItemName || row.brand,
            brand: row.brand,
            unitType: row.unitType,
          },
        }));

      if (insufficient.length > 0) {
        setInsufficientItems(insufficient);
        setValidationDialogMessage(
          "Cannot save. The following items have insufficient inventory:"
        );
        setValidationDialog(true);
        return;
      }
    }

    if (type === "INPUT") {
      setEditInput(false);
    } else {
      setEditOutput(false);
    }

    setLoading(true);
    let itemList = [];
    for (let x = 0; x < items.length; x++) {
      const row = items[x];
      const entry = {
        id: row.lineId,
        orderQty: parseFloat(row.qty),
        isAvailable: row.availability === "Yes" || row.availability === true ? true : false,
        itemUnit: row.orderUnit,
        prodUnitsForItemUnit: parseFloat(row.itemUintsPerOrderUnit),
        lineItemType: type,
      };
      if (!row.lineId && row.importId) {
        entry.importId = row.importId;
        entry.productId = subOrder?.subOrderProduct?.subProductId;
      }
      if (type === "OUTPUT" && row.fromCategoryId != null) {
        entry.effectiveMaterialImportIds = effectiveMaterialsMap[row.fromCategoryId] || [];
      }
      itemList.push(entry);
    }
    let updateResponse = await updateSubOrderLineItem(orderId, itemList);
    if (!updateResponse) {
      setErrors("Failed to save line items. Changes were not saved.");
      setSnackbarOpen(true);
    }
    await fetchData();
    setLoading(false);
  };

  // Payment handlers
  const handleSendForApprovalPayment = async (paymentId) => {
    setLoading(true);
    let sendForApprovalResponse = await sendForApprovalSubOrderPayment(
      paymentId,
      orderId
    );
    if (sendForApprovalResponse) {
      setLoading(false);
      fetchData();
    } else {
      setLoading(false);
      setErrors("Unable to send for approval!");
    }
  };

  const handleApprovePayment = async (paymentId) => {
    setLoading(true);
    let approveResponse = await approveSubOrderPayment(
      orderId,
      paymentId,
      true
    );
    if (approveResponse) {
      setLoading(false);
      fetchData();
    } else {
      setLoading(false);
      setErrors("Unable to approve payment!");
    }
  };

  const handleRejectPayment = async (paymentId) => {
    setLoading(true);
    let approveResponse = await approveSubOrderPayment(
      orderId,
      paymentId,
      false
    );
    if (approveResponse) {
      setLoading(false);
      fetchData();
    } else {
      setLoading(false);
      setErrors("Unable to reject payment!");
    }
  };

  const handlePayPayment = async (paymentId) => {
    setLoading(true);
    let payResponse = await markAsPaidSubOrderPayment(paymentId);
    if (payResponse) {
      setLoading(false);
      fetchData();
    } else {
      setLoading(false);
      setErrors("Unable to process payment!");
    }
  };

  // ==========================================
  // SUB ORDER APPROVAL HANDLERS
  // ==========================================

  const handleSendForApproval = async () => {
    setLoading(true);
    console.log("[SEND FOR APPROVAL] Starting validation for order:", orderId);

    try {
      const validationResult = await validateSubOrder(orderId);
      console.log("[VALIDATION RESULT]", validationResult);

      if (validationResult === "Something went wrong...") {
        setErrors("Unable to validate inventory. Please try again.");
        setLoading(false);
        return;
      }

      if (Array.isArray(validationResult) && validationResult.length === 0) {
        console.log("[VALIDATION] All items have sufficient inventory ✓");
        const sendApprovalResult = await sendForApproval(orderId);

        if (sendApprovalResult) {
          console.log("[SEND FOR APPROVAL] Success! Inventory reserved.");
          alert(
            "Order sent for approval successfully! INPUT inventory has been reserved."
          );
          fetchData();
        } else {
          setErrors("Unable to send order for approval. Please try again.");
        }
      } else {
        console.log(
          "[VALIDATION] Insufficient inventory for",
          validationResult.length,
          "items ✗"
        );
        setInsufficientItems(validationResult);
        setValidationDialogMessage(
          "Cannot send for approval. The following items have insufficient inventory:"
        );
        setValidationDialog(true);
      }
    } catch (error) {
      console.error("[SEND FOR APPROVAL] Error:", error);
      setErrors("An error occurred while sending for approval.");
    } finally {
      setLoading(false);
    }
  };

  const handleApproveOrder = () => {
    setApprovalType("approve");
    setRejectionReason("");
    setApprovalDialog(true);
  };

  const handleRejectOrder = () => {
    setApprovalType("reject");
    setRejectionReason("");
    setApprovalDialog(true);
  };

  const handleConfirmApproval = async () => {
    setLoading(true);
    setApprovalDialog(false);

    try {
      const isApproved = approvalType === "approve";
      console.log(`[${approvalType.toUpperCase()}] Order:`, orderId);

      const result = await subOrderApproval(orderId, isApproved);

      if (result) {
        if (isApproved) {
          alert(
            "Order approved successfully! INPUT inventory reservation confirmed."
          );
        } else {
          alert("Order rejected. Reserved INPUT inventory has been released.");
        }
        fetchData();
      } else {
        setErrors(`Unable to ${approvalType} order. Please try again.`);
      }
    } catch (error) {
      console.error(`[${approvalType.toUpperCase()}] Error:`, error);
      setErrors(`An error occurred while ${approvalType}ing the order.`);
    } finally {
      setLoading(false);
      setRejectionReason("");
    }
  };

  const handleCompleteOrder = async () => {
    setLoading(true);
    try {
      const result = await completeSubOrder(orderId);
      if (result) {
        const freshOrder = await fetchData();
        alert(
          freshOrder?.paymentStatus === "PAID"
            ? "Order marked as fully completed and paid!"
            : "Order marked as complete. Payment is still outstanding."
        );
      } else {
        setErrors("Unable to complete order. Please try again.");
      }
    } catch (error) {
      console.error("[COMPLETE ORDER ERROR]", error);
      setErrors("An error occurred while completing the order.");
    } finally {
      setLoading(false);
    }
  };

  // ==========================================
  // NEW HANDLERS FOR RECEIVING & CHECKING
  // ==========================================

  const handleOpenReceiveDialog = (lineItem) => {
    setSelectedLineItem(lineItem);
    setReceiveDialogOpen(true);
  };

  const handleReceiveOutput = async (receiveData) => {
    setLoading(true);
    try {
      const result = await receiveOutputLineItem(receiveData);

      if (result) {
        await fetchData();
        alert(`Successfully received ${receiveData.receivedQty} units!`);
        setReceiveDialogOpen(false);
        setSelectedLineItem(null);
      } else {
        setErrors("Unable to receive items. Please try again.");
      }
    } catch (error) {
      console.error("[RECEIVE ERROR]", error);
      setErrors("An error occurred while receiving items.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewHistory = async (lineItem) => {
    setLoading(true);
    setSelectedLineItem(lineItem);

    try {
      const receives = await getLineItemReceives(lineItem.lineId);

      if (receives && Array.isArray(receives)) {
        setLineItemReceives(receives);
        setHistoryDialogOpen(true);
      } else {
        setErrors("Unable to fetch receiving history.");
      }
    } catch (error) {
      console.error("[HISTORY ERROR]", error);
      setErrors("An error occurred while fetching history.");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCheckDialog = (receiveBatch) => {
    setSelectedReceiveBatch(receiveBatch);
    setCheckDialogOpen(true);
  };

  const handleCheckBatch = async (checkData) => {
    setLoading(true);
    try {
      const result = await checkReceivedBatch(checkData);

      if (result) {
        console.log("[CHECK SUCCESS]", result);
        alert(
          `Successfully checked: ${checkData.checkedQty} good, ${checkData.damagedQty} damaged. ${checkData.checkedQty} units added to inventory!`
        );

        await fetchData();

        if (selectedLineItem) {
          const receives = await getLineItemReceives(selectedLineItem.lineId);
          if (receives) {
            setLineItemReceives(receives);
          }
        }

        setCheckDialogOpen(false);
        setSelectedReceiveBatch(null);
      } else {
        setErrors("Unable to check batch. Please try again.");
      }
    } catch (error) {
      console.error("[CHECK ERROR]", error);
      setErrors("An error occurred while checking batch.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseReceiveDialog = () => {
    setReceiveDialogOpen(false);
    setSelectedLineItem(null);
  };

  const handleCloseCheckDialog = () => {
    setCheckDialogOpen(false);
    setSelectedReceiveBatch(null);
  };

  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setSelectedLineItem(null);
    setLineItemReceives([]);
  };

  // ==========================================
  // RECONSTRUCTION HANDLERS
  // ==========================================

  const handleCreateReconstruction = (checkRecord) => {
    console.log("[CREATE RECONSTRUCTION] For check:", checkRecord);
    setSelectedCheckRecord(checkRecord);
    setReconstructionDialogOpen(true);
  };

  const handleViewReconstructions = async (checkId) => {
    console.log("[VIEW RECONSTRUCTIONS] For check ID:", checkId);
    setLoading(true);

    try {
      const reconstructions = await getReconstructionsByCheckId(checkId);

      if (reconstructions && Array.isArray(reconstructions)) {
        console.log("[RECONSTRUCTIONS]", reconstructions);
        setReconstructionHistory(reconstructions);

        // Find the check record from damaged batches
        const checkRecord = damagedBatches.find(
          (batch) => batch.id === checkId
        );
        setSelectedCheckRecord(checkRecord);
        setReconstructionHistoryOpen(true);
      } else {
        setErrors("Unable to fetch reconstruction history.");
      }
    } catch (error) {
      console.error("[RECONSTRUCTION HISTORY ERROR]", error);
      setErrors("An error occurred while fetching reconstruction history.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReconstruction = async (reconstructionData) => {
    setLoading(true);
    console.log("[SUBMIT RECONSTRUCTION]", reconstructionData);

    try {
      const result = await createReconstructionBatch(reconstructionData);

      if (result) {
        console.log("[RECONSTRUCTION SUCCESS]", result);

        const inputMaterialsMsg =
          reconstructionData.inputItems.length > 0
            ? `\n${reconstructionData.inputItems.length} input material(s) reserved from inventory.`
            : "\nNo input materials (manual reconstruction).";

        alert(
          `Reconstruction batch created!\n` +
            `${reconstructionData.reconstructedQty} units will be reconstructed.${inputMaterialsMsg}`
        );

        // Refresh all data
        await fetchData();

        // Close dialog
        setReconstructionDialogOpen(false);
        setSelectedCheckRecord(null);
      } else {
        setErrors("Unable to create reconstruction batch. Please try again.");
      }
    } catch (error) {
      console.error("[RECONSTRUCTION ERROR]", error);
      setErrors("An error occurred while creating reconstruction batch.");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateReconstructionStatus = async (reconstructionId, status) => {
    setLoading(true);
    console.log(
      `[UPDATE STATUS] Reconstruction ${reconstructionId} -> ${status}`
    );

    try {
      const result = await updateReconstructionStatus(reconstructionId, status);

      if (result) {
        console.log("[UPDATE STATUS SUCCESS]", result);
        alert(`Reconstruction #${reconstructionId} marked as ${status}`);

        // Refresh reconstruction history
        if (selectedCheckRecord) {
          const reconstructions = await getReconstructionsByCheckId(
            selectedCheckRecord.id
          );
          if (reconstructions) {
            setReconstructionHistory(reconstructions);
          }
        }

        // Refresh all data
        await fetchData();
      } else {
        setErrors("Unable to update reconstruction status.");
      }
    } catch (error) {
      console.error("[UPDATE STATUS ERROR]", error);
      setErrors("An error occurred while updating status.");
    } finally {
      setLoading(false);
    }
  };

  const handleCloseReconstructionDialog = () => {
    setReconstructionDialogOpen(false);
    setSelectedCheckRecord(null);
  };

  const handleCloseReconstructionHistory = () => {
    setReconstructionHistoryOpen(false);
    setSelectedCheckRecord(null);
    setReconstructionHistory([]);
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "70vh",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">
          Loading sub order details...
        </Typography>
      </Box>
    );
  }

  if (!subOrder) {
    return (
      <Paper
        elevation={3}
        sx={{
          p: 4,
          m: 3,
          textAlign: "center",
          borderRadius: 2,
          backgroundColor: alpha(theme.palette.error.light, 0.1),
        }}
      >
        <Box sx={{ mb: 2 }}>
          <DescriptionIcon color="error" sx={{ fontSize: 48 }} />
        </Box>
        <Typography variant="h6" color="text.secondary" gutterBottom>
          Sub Order Not Found
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          The requested sub order data could not be retrieved or does not exist.
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
        >
          Go Back
        </Button>
      </Paper>
    );
  }

  const allOutputFullyReceived =
    receivingProgress.length > 0 &&
    receivingProgress.every((p) => p.isFullyReceived);

  return (
    <Paper
      elevation={3}
      sx={{ p: { xs: 2, md: 2 }, m: { xs: 1, md: 2 }, borderRadius: 2 }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <ShoppingCartIcon fontSize="large" color="primary" />
          <Typography variant="h5" fontWeight="600" color="primary.main">
            Sub Order Details
          </Typography>
        </Box>
        <Tooltip title="Close">
          <IconButton
            onClick={() => history.goBack()}
            aria-label="close"
            sx={{
              bgcolor: alpha(theme.palette.grey[500], 0.1),
              "&:hover": { bgcolor: alpha(theme.palette.grey[500], 0.2) },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Divider sx={{ mb: 4 }} />

      {/* Order Header Information */}
      <Grid container spacing={2} sx={{ mb: 4 }}>
        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <TagIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Order Information
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Order ID
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {subOrder.orderId}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Order Date
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CalendarTodayIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body1">
                      {formatDate(subOrder.orderDate)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Added Date
                  </Typography>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                    <CalendarTodayIcon
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography variant="body1">
                      {formatDate(subOrder.addedDate)}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Status
                  </Typography>
                  {getStatusChip(subOrder.status)}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Payment Status
                  </Typography>
                  {getPaymentStatusChip(subOrder.paymentStatus)}
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body2" color="text.secondary">
                    Total Items
                  </Typography>
                  <Typography variant="body1" fontWeight="600">
                    {subOrder.lineItems?.length || 0}
                  </Typography>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={6}>
          <Card elevation={1} sx={{ height: "100%", borderRadius: 2 }}>
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                <AttachMoneyIcon sx={{ mr: 1, color: "primary.main" }} />
                <Typography
                  variant="subtitle1"
                  fontWeight="700"
                  color="primary.main"
                >
                  Financial Details
                </Typography>
              </Box>
              <Grid container spacing={1.5}>
                {/* Cost breakdown rows */}
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Material Cost
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {formatCurrency(subOrder.materialCost ?? 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Labour Cost
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {formatCurrency(subOrder.labourCost ?? 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Transport Cost
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {formatCurrency(subOrder.transportCost ?? 0)}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">
                    Order Cost
                  </Typography>
                  <Typography variant="body2" fontWeight="600">
                    {formatCurrency(subOrder.cost ?? 0)}
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 0.5 }} />
                </Grid>

                {/* Totals */}
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.success.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Total Cost
                    </Typography>
                    <Typography variant="h6" fontWeight="700" color="success.main">
                      {formatCurrency(
                        (subOrder.cost ?? 0) +
                          (subOrder.materialCost ?? 0) +
                          (subOrder.labourCost ?? 0) +
                          (subOrder.transportCost ?? 0)
                      )}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6}>
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      bgcolor: alpha(theme.palette.info.main, 0.1),
                      borderRadius: 2,
                    }}
                  >
                    <Typography variant="body2" color="text.secondary">
                      Paid Amount
                    </Typography>
                    <Typography variant="h6" fontWeight="700" color="info.main">
                      {formatCurrency(subOrder.paidAmount)}
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Sub order person Information */}
      {subOrder.subOrderPerson && (
        <Card elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <PersonIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color="primary.main"
              >
                Sub Order Person Information
              </Typography>
              {getStatusChip(subOrder.subOrderPerson.status)}
            </Box>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}
                >
                  <Avatar sx={{ width: 32, height: 32 }}>
                    <PersonIcon />
                  </Avatar>
                  <Box>
                    <Typography variant="body1" fontWeight="600">
                      {subOrder.subOrderPerson.personName}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      ID: {subOrder.subOrderPerson.personId}
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 1,
                  }}
                >
                  <PhoneIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="body2">
                    {subOrder.subOrderPerson.phone}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    mb: 1,
                  }}
                >
                  <EmailIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  <Typography variant="body2">
                    {subOrder.subOrderPerson.email}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <LocationOnIcon
                    sx={{ fontSize: 16, color: "text.secondary" }}
                  />
                  <Typography variant="body2">
                    {subOrder.subOrderPerson.address}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      )}

      {/* Input Order Items Table */}
      <Card elevation={1} sx={{ mb: 4, borderRadius: 2, overflow: "hidden" }}>
        <CardContent>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <ShoppingBasketIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color="primary.main"
              >
                Input Order Items
              </Typography>
              <Badge
                badgeContent={(inputItems?.length || 0) + (inputCategoryItems?.length || 0)}
                color="primary"
                sx={{ ml: 1 }}
              />
            </Box>
            <Box sx={{ display: "flex", gap: 1 }}>
              {!editInput ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<EditIcon />}
                  onClick={() => setEditInput(true)}
                  // disabled={
                  //   subOrder.status !== "DRAFT" &&
                  //   subOrder.status !== "AWAITING_APPROVAL"
                  // }
                >
                  Edit
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<SaveIcon />}
                  onClick={() => saveLineItems("INPUT", inputItems)}
                >
                  Save
                </Button>
              )}
            </Box>
          </Box>
          <Grid item xs={12} md={12}>
            {inputItems.length > 0 && (
              editInput ? (
                <BillWritableTable
                  columns={columnsForInput}
                  rows={inputItems}
                  setRows={handleInputRowsChange}
                  page={writeable_page}
                  setPage={set_writeable_Page}
                  rowsPerPage={writeable_rowsPerPage}
                  tablePagin={false}
                  setRowsPerPage={set_writeable_RowsPerPage}
                  onDeleteRow={(row) => handleDeleteCategoryItem(row, "INPUT")}
                />
              ) : (
                <TableItem
                  setRow={null}
                  dropDown={false}
                  columns={columnsForInput}
                  rows={inputItems}
                />
              )
            )}

            {inputCategoryItems.length > 0 && (
              <Box sx={{ mt: inputItems.length > 0 ? 3 : 0 }}>
                <Typography
                  variant="body2"
                  fontWeight="600"
                  color="text.secondary"
                  sx={{ mb: 1 }}
                >
                  Input Categories
                </Typography>
                <TableContainer
                  sx={{
                    bgcolor: alpha(theme.palette.background.default, 0.5),
                    borderRadius: 1,
                  }}
                >
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.08) }}>
                        <TableCell sx={{ fontWeight: 600 }}>Category</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 600 }}>
                          {editInput ? "Select Items" : "View Items"}
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {inputCategoryItems.map((cat) => (
                        <TableRow key={cat.categoryId}>
                          <TableCell>{cat.categoryName}</TableCell>
                          <TableCell align="right">
                            {editInput ? (
                              <Tooltip title="Select import items from this category">
                                <IconButton
                                  size="small"
                                  color="secondary"
                                  onClick={() => handleOpenCategoryItemSelect(cat.categoryId, cat.categoryName, "INPUT")}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Tooltip title="View import items in this category">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => handleViewCategoryItems(cat.categoryId, cat.categoryName)}
                                >
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}
          </Grid>
        </CardContent>
      </Card>

      {/* NEW: Damaged Items Panel */}
      {console.log(
        "[RENDER] Damaged batches state:",
        damagedBatches,
        "Length:",
        damagedBatches?.length
      )}
      {damagedBatches && damagedBatches.length > 0 ? (
        <>
          {console.log("[RENDER] Showing DamagedItemsPanel")}
          <DamagedItemsPanel
            damagedBatches={damagedBatches}
            onCreateReconstruction={handleCreateReconstruction}
            onViewReconstructions={handleViewReconstructions}
          />
        </>
      ) : (
        console.log(
          "[RENDER] NOT showing DamagedItemsPanel - no damaged batches"
        )
      )}

      {/* Pending Deletion Requests — visible to all, approve/reject only for authorized */}
      {(() => {
        const pendingItems = [
          ...inputItems.filter((r) => r.pendingDeletion).map((r) => ({ ...r, itemType: "INPUT" })),
          ...outputItems.filter((r) => r.pendingDeletion).map((r) => ({ ...r, itemType: "OUTPUT" })),
        ];
        if (pendingItems.length === 0) return null;
        const isAuthorized = hasAnyRole(["SUB_ORDER_CRITICAL_MANAGEMENT", "ROLE_ADMIN"]);
        return (
          <Card
            elevation={1}
            sx={{
              mb: 4,
              borderRadius: 2,
              border: `1px solid ${theme.palette.warning.main}`,
              bgcolor: alpha(theme.palette.warning.main, 0.03),
            }}
          >
            <CardContent>
              <Box sx={{ display: "flex", alignItems: "center", mb: 2, gap: 1 }}>
                <WarningAmberIcon color="warning" />
                <Typography variant="subtitle1" fontWeight="700" color="warning.main">
                  Pending Deletion Requests ({pendingItems.length})
                </Typography>
                {!isAuthorized && (
                  <Chip
                    size="small"
                    label="Awaiting authorized approval"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Box>
              {pendingItems.map((item) => (
                <Box
                  key={item.lineId}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    mb: 1,
                    p: 1.5,
                    borderRadius: 1,
                    bgcolor: alpha(theme.palette.warning.main, 0.07),
                    border: `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
                  }}
                >
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="body2" fontWeight="600">
                      {item.rawItemName}
                    </Typography>
                    <Chip
                      size="small"
                      label={item.itemType}
                      color={item.itemType === "INPUT" ? "primary" : "secondary"}
                      variant="outlined"
                      sx={{ mt: 0.5, height: 18, fontSize: "0.65rem" }}
                    />
                  </Box>
                  {isAuthorized ? (
                    <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
                      <Button
                        size="small"
                        color="error"
                        variant="contained"
                        onClick={() => handleOpenPendingApproval(item, "approve")}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        color="warning"
                        variant="outlined"
                        onClick={() => handleOpenPendingApproval(item, "reject")}
                      >
                        Reject
                      </Button>
                    </Box>
                  ) : (
                    <Chip
                      size="small"
                      label="Pending"
                      color="warning"
                      sx={{ flexShrink: 0 }}
                    />
                  )}
                </Box>
              ))}
            </CardContent>
          </Card>
        );
      })()}

      {/* NEW: Enhanced Output Order Items Table */}
      <OutputItemsTable
        outputItems={outputItems}
        setOutputItems={setOutputItems}
        editOutput={editOutput}
        setEditOutput={setEditOutput}
        onSave={() => saveLineItems("OUTPUT", outputItems)}
        canEdit={
          subOrder.status === "DRAFT" ||
          subOrder.status === "AWAITING_APPROVAL" ||
          outputCategoryItems.length > 0
        }
        canReceive={
          subOrder.status !== "DRAFT" &&
          subOrder.status !== "AWAITING_APPROVAL"
        }
        onReceive={handleOpenReceiveDialog}
        onViewHistory={handleViewHistory}
        receivingProgress={receivingProgress}
        columnsForOutput={columnsForOutput}
        writeable_page={writeable_page}
        set_writeable_Page={set_writeable_Page}
        writeable_rowsPerPage={writeable_rowsPerPage}
        set_writeable_RowsPerPage={set_writeable_RowsPerPage}
        categoryItems={outputCategoryItems}
        onViewCategory={handleViewCategoryItems}
        onSelectCategoryItems={(catId, catName) =>
          handleOpenCategoryItemSelect(catId, catName, "OUTPUT")
        }
        onDeleteRow={(row) => handleDeleteCategoryItem(row, "OUTPUT")}
        outputItemCosts={outputItemCosts}
      />

      {/* Payment Information */}
      <RoleBasedRender allowedRoles={["SUB_ORDER_PAYMENT_VIEW", "ROLE_ADMIN"]}>
        <Card elevation={1} sx={{ mb: 4, borderRadius: 2 }}>
          <CardContent>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <PaymentIcon sx={{ mr: 1, color: "primary.main" }} />
              <Typography
                variant="subtitle1"
                fontWeight="700"
                color="primary.main"
              >
                Payment Information
              </Typography>
              <Badge
                badgeContent={subOrder.subOrderPayment?.length || 0}
                color="primary"
                sx={{ ml: 1 }}
              />
            </Box>
            {/* Payments are only accepted once an order is approved (post DRAFT/AWAITING_APPROVAL/REJECTED). */}
            {!["DRAFT", "AWAITING_APPROVAL", "REJECTED"].includes(subOrder.status) && (
              <RoleBasedRender
                allowedRoles={["SUB_ORDER_PAYMENT_CREATE", "ROLE_ADMIN"]}
              >
                <Box
                  sx={{
                    maxWidth: "20%",
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 2,
                  }}
                >
                  <SelectButton
                    startIcon={<AddPaymentIcon />}
                    onClick={handleOpenPaymentModal}
                  >
                    Add Payment
                  </SelectButton>
                </Box>
              </RoleBasedRender>
            )}
            {paymentList?.length === 0 ? (
              <Box sx={{ textAlign: "center", py: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  No payment records found
                </Typography>
              </Box>
            ) : (
              <SubOrderPaymentCard
                paymentList={paymentList}
                onDelete={handleDeleteClick}
                onSendForApproval={handleSendForApprovalPayment}
                onApprove={handleApprovePayment}
                onReject={handleRejectPayment}
                onPay={handlePayPayment}
                formatCurrency={formatCurrency}
                filterDate={filterDate}
              />
            )}
          </CardContent>
        </Card>
      </RoleBasedRender>

      {/* Action Buttons */}
      <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => history.goBack()}
        >
          Go Back
        </Button>

        {subOrder.status === "DRAFT" && (
          <RoleBasedRender allowedRoles={["SUB_ORDER_CREATE", "ROLE_ADMIN"]}>
            <Button
              onClick={handleSendForApproval}
              variant="contained"
              color="primary"
              startIcon={<SendIcon />}
              sx={{ px: 3 }}
            >
              Send for Approval
            </Button>
          </RoleBasedRender>
        )}

        {subOrder.status === "AWAITING_APPROVAL" && (
          <RoleBasedRender allowedRoles={["SUB_ORDER_APPROVAL", "ROLE_ADMIN"]}>
            <Button
              onClick={handleApproveOrder}
              variant="contained"
              color="success"
              startIcon={<DoneIcon />}
              sx={{ px: 3 }}
            >
              Approve
            </Button>
          </RoleBasedRender>
        )}

        {subOrder.status === "AWAITING_APPROVAL" && (
          <RoleBasedRender allowedRoles={["SUB_ORDER_APPROVAL", "ROLE_ADMIN"]}>
            <Button
              onClick={handleRejectOrder}
              variant="contained"
              color="error"
              startIcon={<CloseIcon />}
              sx={{ px: 3 }}
            >
              Reject
            </Button>
          </RoleBasedRender>
        )}

        {subOrder.status !== "COMPLETED" && allOutputFullyReceived && (
          <RoleBasedRender allowedRoles={["SUB_ORDER_CREATE", "ROLE_ADMIN"]}>
            <Button
              onClick={handleCompleteOrder}
              variant="contained"
              color="success"
              startIcon={<CheckCircleIcon />}
              sx={{ px: 3 }}
            >
              Complete Order
            </Button>
          </RoleBasedRender>
        )}

        <RoleBasedRender allowedRoles={["HIGHER_SUB_ORDER_DELETE", "ROLE_ADMIN"]}>
          <Button
            onClick={() => { setForceDeleteConfirmText(""); setForceDeleteOpen(true); }}
            variant="contained"
            color="error"
            startIcon={<DeleteForeverIcon />}
            sx={{ px: 3 }}
          >
            Wipe Order
          </Button>
        </RoleBasedRender>
      </Box>

      {/* Validation Dialog */}
      <Dialog
        open={validationDialog}
        onClose={() => setValidationDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmberIcon color="error" />
            <Typography variant="h6">Insufficient Inventory</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {validationDialogMessage}
          </Typography>
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Item Name</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell align="right">Required</TableCell>
                  <TableCell align="right">Available</TableCell>
                  <TableCell align="right">Shortfall</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {insufficientItems.map((item, index) => {
                  const requiredQty = (item?.orderedQty ?? 0) * (item?.prodUnitsForItemUnit ?? 1);
                  const availableQty = item?.imports?.qty ?? 0;
                  const shortfall = requiredQty - availableQty;
                  const unit = item?.imports?.unitType ?? "";
                  return (
                    <TableRow key={index}>
                      <TableCell>{item?.imports?.itemName}</TableCell>
                      <TableCell>{item?.imports?.brand}</TableCell>
                      <TableCell align="right">
                        {requiredQty.toFixed(2)} {unit}
                      </TableCell>
                      <TableCell align="right">
                        {availableQty.toFixed(2)} {unit}
                      </TableCell>
                      <TableCell align="right" sx={{ color: "error.main" }}>
                        {shortfall.toFixed(2)} {unit}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setValidationDialog(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Approval/Rejection Dialog */}
      <Dialog
        open={approvalDialog}
        onClose={() => setApprovalDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {approvalType === "approve" ? (
              <CheckCircleIcon color="success" />
            ) : (
              <WarningAmberIcon color="error" />
            )}
            <Typography variant="h6">
              {approvalType === "approve" ? "Approve Order" : "Reject Order"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            {approvalType === "approve"
              ? `Are you sure you want to approve Sub Order #${orderId}? INPUT inventory reservation will be confirmed.`
              : `Are you sure you want to reject Sub Order #${orderId}? Reserved INPUT inventory will be released.`}
          </Typography>
          {approvalType === "reject" && (
            <TextField
              fullWidth
              multiline
              rows={3}
              label="Rejection Reason (Optional)"
              variant="outlined"
              value={rejectionReason}
              onChange={(e) => setRejectionReason(e.target.value)}
              sx={{ mt: 2 }}
              placeholder="Enter reason for rejection..."
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApprovalDialog(false)}>Cancel</Button>
          <Button
            onClick={handleConfirmApproval}
            color={approvalType === "approve" ? "success" : "error"}
            variant="contained"
          >
            {approvalType === "approve" ? "Approve" : "Reject"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Payment Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
      >
        <DialogTitle>Confirm Payment Deletion</DialogTitle>
        <DialogContent>
          <Typography gutterBottom>
            Payment ID: <strong>{selectedPaymentId}</strong>
            <Tooltip title="Copy">
              <IconButton size="small" onClick={handleCopyId}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Typography>
          <TextField
            fullWidth
            label="Enter Payment ID to Confirm"
            variant="outlined"
            value={confirmationInput}
            onChange={(e) => setConfirmationInput(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDeleteDialog(false)}>Cancel</Button>
          <Button
            color="error"
            disabled={confirmationInput !== String(selectedPaymentId)}
            onClick={handleDeleteConfirm}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Effective Materials Selection Dialog */}
      <EffectiveMaterialsDialog
        open={effectiveMaterialsDialogOpen}
        onClose={() => setEffectiveMaterialsDialogOpen(false)}
        onConfirm={handleConfirmEffectiveMaterials}
        categoryName={currentEffectiveCategory?.catName || ""}
        categoryInputItems={inputItems.filter(
          (i) => i.fromCategoryId != null
        )}
        selectedImportIds={
          effectiveMaterialsMap[currentEffectiveCategory?.catId] || []
        }
        nonCategoryInputCount={inputItems.filter((i) => i.fromCategoryId == null).length}
      />

      {/* NEW DIALOGS FOR RECEIVING & CHECKING */}
      <ReceiveOutputDialog
        subOrderPerson={subOrder.subOrderPerson?.personName || ""}
        open={receiveDialogOpen}
        onClose={handleCloseReceiveDialog}
        lineItem={selectedLineItem}
        onReceive={handleReceiveOutput}
      />

      <CheckReceivedBatchDialog
        open={checkDialogOpen}
        onClose={handleCloseCheckDialog}
        receiveBatch={selectedReceiveBatch}
        onCheck={handleCheckBatch}
      />

      <ReceivingHistoryDialog
        open={historyDialogOpen}
        onClose={handleCloseHistoryDialog}
        lineItem={selectedLineItem}
        receives={lineItemReceives}
        onCheckBatch={handleOpenCheckDialog}
      />

      {/* NEW: RECONSTRUCTION DIALOGS */}
      <ReconstructionDialog
        open={reconstructionDialogOpen}
        onClose={handleCloseReconstructionDialog}
        checkRecord={selectedCheckRecord}
        onReconstruct={handleSubmitReconstruction}
      />

      <ReconstructionHistoryDialog
        open={reconstructionHistoryOpen}
        onClose={handleCloseReconstructionHistory}
        checkRecord={selectedCheckRecord}
        reconstructions={reconstructionHistory}
        onUpdateStatus={handleUpdateReconstructionStatus}
      />

      {enablePayment && (
        <SubOrderPayment
          orderId={orderId}
          setEnablePayment={setEnablePayment}
          enablePayment={enablePayment}
          payments={paymentList}
          setPayments={setPaymentList}
        />
      )}

      {/* Wipe Order Confirmation Dialog */}
      <Dialog
        open={forceDeleteOpen}
        onClose={() => !forceDeleteLoading && setForceDeleteOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmberIcon color="error" />
            <Typography variant="h6">Permanently Delete Sub Order</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            This will permanently wipe the sub order and reverse all effects:
            <ul style={{ margin: "8px 0 0 0", paddingLeft: 20 }}>
              <li>All INPUT stock intake reservations reverted</li>
              <li>All OUTPUT stock intakes from quality checks reverted</li>
              <li>All reconstruction material intakes reverted</li>
              <li>All payment records deleted</li>
              <li>All receiving, checking, and reconstruction records deleted</li>
              <li>Price records created by this order reversed</li>
            </ul>
          </Alert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Type the Order ID <strong>{orderId}</strong> to confirm:
          </Typography>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            value={forceDeleteConfirmText}
            onChange={(e) => setForceDeleteConfirmText(e.target.value)}
            placeholder={`Enter ${orderId}`}
            error={forceDeleteConfirmText.length > 0 && forceDeleteConfirmText !== String(orderId)}
            helperText={
              forceDeleteConfirmText.length > 0 && forceDeleteConfirmText !== String(orderId)
                ? "Order ID does not match"
                : ""
            }
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setForceDeleteOpen(false)}
            disabled={forceDeleteLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleForceDelete}
            color="error"
            variant="contained"
            startIcon={forceDeleteLoading ? <CircularProgress size={16} color="inherit" /> : <DeleteForeverIcon />}
            disabled={forceDeleteConfirmText !== String(orderId) || forceDeleteLoading}
          >
            {forceDeleteLoading ? "Deleting..." : "Permanently Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete / Request-Deletion Dialog */}
      <Dialog
        open={deleteCategoryItemDialogOpen}
        onClose={() => setDeleteCategoryItemDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <WarningAmberIcon color="warning" />
            <Typography variant="h6">
              {!pendingDeleteRow?.lineId || subOrder?.status === "DRAFT"
                ? "Remove Item"
                : pendingDeleteRow?.pendingDeletion
                ? "Deletion Already Requested"
                : "Request Item Removal"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          {pendingDeleteRow?.pendingDeletion ? (
            <Alert severity="info">
              This item already has a pending deletion request awaiting approval
              from an authorized user.
            </Alert>
          ) : !pendingDeleteRow?.lineId || subOrder?.status === "DRAFT" ? (
            <Typography variant="body1">
              Are you sure you want to remove this item from the order?
            </Typography>
          ) : (
            <>
              <Typography variant="body1" gutterBottom>
                This will send a deletion request to an authorized user for
                approval. The item will remain in the order until the request is
                approved.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteCategoryItemDialogOpen(false)}>
            Cancel
          </Button>
          {!pendingDeleteRow?.pendingDeletion && (
            <Button
              color={subOrder?.status === "DRAFT" || !pendingDeleteRow?.lineId ? "error" : "warning"}
              variant="contained"
              onClick={handleConfirmDeleteCategoryItem}
            >
              {!pendingDeleteRow?.lineId || subOrder?.status === "DRAFT"
                ? "Remove"
                : "Send Request"}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Pending Deletion Approval Dialog (for authorized users) */}
      <Dialog
        open={pendingApprovalDialogOpen}
        onClose={() => setPendingApprovalDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {pendingApprovalAction === "approve" ? (
              <CheckCircleIcon color="error" />
            ) : (
              <WarningAmberIcon color="warning" />
            )}
            <Typography variant="h6">
              {pendingApprovalAction === "approve"
                ? "Approve Deletion Request"
                : "Reject Deletion Request"}
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1">
            {pendingApprovalAction === "approve"
              ? `Are you sure you want to approve the deletion of "${pendingApprovalItem?.rawItemName}"? The item will be permanently removed from the order.`
              : `Are you sure you want to reject the deletion request for "${pendingApprovalItem?.rawItemName}"? The item will remain in the order.`}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingApprovalDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            color={pendingApprovalAction === "approve" ? "error" : "warning"}
            variant="contained"
            onClick={handleSubmitPendingApproval}
          >
            {pendingApprovalAction === "approve" ? "Approve & Delete" : "Reject Request"}
          </Button>
        </DialogActions>
      </Dialog>

      <CategoryItemsViewDialog
        open={categoryViewOpen}
        onClose={() => setCategoryViewOpen(false)}
        categoryName={categoryViewName}
        items={categoryViewItems}
      />

      {openCategoryItemSelect && activeCategoryRow ? (
        <ItemSelectingPopup
          setOpenTable={setOpenCategoryItemSelect}
          openTable={openCategoryItemSelect}
          handleCloseTable={handleCloseCategoryItemSelect}
          setSelectedItems={setCategoryPopupSelectedItems}
          selectedItems={categoryPopupSelectedItems}
          rows={categoryPopupRows}
          setRows={setCategoryPopupRows}
          isOneChoise={false}
          presetCategoryIds={[activeCategoryRow.catId]}
        />
      ) : null}

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={5000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{ width: "100%" }}
        >
          {errors}
        </Alert>
      </Snackbar>
    </Paper>
  );
}

SubOrderView.propTypes = {
  orderId: PropTypes.any,
};

export default SubOrderView;
